import { Fragment, useEffect } from 'react'

import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'

import { languageStorageKey } from '@/services/constants/localStorageKeys'
import { useSafeLocalStorage } from '@/services/hooks/useSafeLocalStorage'
import { useInspection } from '@/services/providers/InspectionContext'

const languagesToDisableI18n = ['en', 'fr']

const countryToLanguage = new Map([
  ['BR', 'pt-BR'],
  ['PT', 'pt-PT'],
  ['ES', 'es'],
  ['UY', 'es-419'],
  ['PY', 'es-419']
])

// Get browser language and map to supported language
const getBrowserLanguage = (): string => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'pt-BR'

  // Check exact match first
  if (['pt-BR', 'pt-PT', 'es', 'es-419'].includes(browserLang)) {
    return browserLang
  }

  // Check language prefix
  if (browserLang.startsWith('pt')) {
    return 'pt-BR'
  }
  if (browserLang.startsWith('es')) {
    return 'es-419' // Default to Latin American Spanish
  }

  // Fallback to pt-BR
  return 'pt-BR'
}

// Determine initial language based on priority:
// 1. Stored language preference (from localStorage)
// 2. Country from inspection (if available and compatible)
// 3. Browser language (mapped to supported languages)
const getInitialLanguage = (inspectionCountry?: string): string => {
  // Priority 1: User's stored language preference
  const storedLanguage = useSafeLocalStorage.get(languageStorageKey)
  if (storedLanguage) {
    return storedLanguage
  }

  // Priority 2: Inspection country
  if (inspectionCountry && countryToLanguage.has(inspectionCountry)) {
    return countryToLanguage.get(inspectionCountry) || 'pt-BR'
  }

  // Priority 3: Browser language
  return getBrowserLanguage()
}

const LanguageCookieObserver: React.FC = () => {
  const [cookies] = useCookies(['googtrans'])
  const { i18n } = useTranslation()

  const { inspection } = useInspection()

  // Set initial language on mount (important for pages without inspection like /not-found)
  useEffect(() => {
    let cancelled = false
    const targetLanguage = getInitialLanguage(undefined)

    if (i18n.language !== targetLanguage) {
      queueMicrotask(() => {
        if (!cancelled) i18n.changeLanguage(targetLanguage)
      })
    }

    return () => {
      cancelled = true
    }
  }, [])

  // Update language when inspection country is loaded
  useEffect(() => {
    let cancelled = false

    if (inspection?.country) {
      const storedLanguage = useSafeLocalStorage.get(languageStorageKey)

      // Only apply country language if user hasn't set a preference
      if (!storedLanguage) {
        const countryLanguage = countryToLanguage.get(inspection.country)

        if (countryLanguage && i18n.language !== countryLanguage) {
          queueMicrotask(() => {
            if (!cancelled) i18n.changeLanguage(countryLanguage)
          })
        }
      }
    }

    return () => {
      cancelled = true
    }
  }, [inspection?.country])

  useEffect(() => {
    let cancelled = false

    if (typeof cookies.googtrans === 'string') {
      const parts = cookies.googtrans.split('/')
      const currentLanguage = parts[parts.length - 1]

      if (currentLanguage && languagesToDisableI18n.includes(currentLanguage)) {
        queueMicrotask(() => {
          if (!cancelled) i18n.changeLanguage('NONE') // disables i18n translations by passing an unknown language string & triggers fallback messages
        })
      }
    }

    return () => {
      cancelled = true
    }
  }, [cookies.googtrans])

  useEffect(() => {
    const handleStorageChange = () => {
      const storedLanguage = useSafeLocalStorage.get(languageStorageKey)

      queueMicrotask(() => {
        i18n.changeLanguage(
          storedLanguage || countryToLanguage.get(inspection?.country || 'BR')
        )
      })
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [inspection?.country])

  return <Fragment />
}

export default LanguageCookieObserver
